import {
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  QueryList,
  SimpleChanges,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { AppState } from 'app/app.reducer';
import {OutletReport, SearchProps} from 'app/interfaces';
import { FacetSearch, Search } from 'app/models';
import { MyCoverageService } from 'app/services';
import { ExportFilenameService } from 'app/components';
import { eq, find } from 'lodash';
import { Subscription } from 'rxjs';
import { Table } from 'primeng/table';

import * as actions from 'redux/actions';
import { Paginator } from 'primeng/paginator';
import {ReportTableActions} from '../report-table-actions';



@Component({
  selector: 'app-outlets-tab',
  templateUrl: './outlets-tab.component.html',
  styleUrls: ['../my-coverage-reporting.component.scss'],
})
export class OutletsTabComponent extends ReportTableActions implements OnInit, OnChanges, OnDestroy {

  public outletsData: OutletReport[];
  public loading: boolean;
  public pageSize: number;
  public page: number;
  public totalItems: number;
  public totalPages: number;
  public currentPage: number;
  public searchBody: Search;
  public sort: string;
  public sortColumn: string;
  public savingOutletId: number | null = null;
  private savedOutlets: any[] = [];
  private outletsOutletsSub: Subscription;

  @Input() projectId: number;
  @Input() myCompanyQuery: string;
  @Input() contentKeywords: string;
  @Input() coverageReportRequest: SearchProps;
  @Input() reportName: string;
  @ViewChild('paginator', { static: false }) paginator: Paginator;
  @ViewChild('outletsTable', { static: false }) table: Table;
  @ViewChildren(
    'sortNameIcon, sortLastMentionedIcon, sortFirstMentionedIcon, sortArticlesIcon, sortTotalCoverageIcon',
    {
      read: ElementRef,
    }
  )
  sortIcons: QueryList<ElementRef>;

  constructor(
    private myCoverageService: MyCoverageService,
    private store: Store<AppState>,
    exportFilenameService: ExportFilenameService
  ) {
    super(myCoverageService, store, exportFilenameService);
    this.outletsData = [];
    this.loading = false;
    this.pageSize = 25;
    this.page = 1;
    this.totalItems = 0;
    this.totalPages = 0;
    this.currentPage = 1;
  }

  ngOnInit() {
    this.getOutletsData();

    this.store.select('search').subscribe(({ searchBody }) => {
      if (searchBody) {
        this.searchBody = searchBody;
      }
    });

    this.outletsOutletsSub = this.store
      .select('authorsOutletsList')
      .subscribe(({ outlets, isAddingAuthorOutlet, isDeletingAuthorOutlet }) => {
        this.savedOutlets = outlets || [];
        if (!isAddingAuthorOutlet && !isDeletingAuthorOutlet) {
          this.savingOutletId = null;
        }
      });
  }

  ngOnDestroy() {
    if (this.outletsOutletsSub) { this.outletsOutletsSub.unsubscribe(); }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.coverageReportRequest && !changes.coverageReportRequest.firstChange) {
      this.page = 1;
      this.getOutletsData();
    }
  }



  getOutletsData() {
    // this.projectId = 129853;
    if (!this.projectId) {
      return;
    }
    this.loading = true;
    this.myCoverageService
      .getOutletsReport(this.projectId, this.page - 1, this.pageSize, this.sort, this.coverageReportRequest || { timeframe: 'ONE_YEAR' })
      .subscribe(
        (response: {
          content: OutletReport[];
          totalElements: number;
          totalPages: number;
          size: number;
          number: number;
          numberOfElements: number;
        }) => {
          this.outletsData = response.content || [];
          this.totalItems = response.totalElements || 0;
          this.totalPages = response.totalPages || 0;
          this.pageSize = response.size || 50;
          this.currentPage = response.number || 0;
          this.loading = false;
        },
        (error) => {
          console.error('Error fetching outlets data:', error);
          this.outletsData = [];
          this.totalItems = 0;
          this.totalPages = 0;
          this.loading = false;
        }
      );
  }

  getOutletContent(outletItem: OutletReport) {
    const reportRequest: any = this.coverageReportRequest || {};
    const reportFrom = reportRequest.periodFrom || reportRequest.from;
    const reportTo = reportRequest.periodTo || reportRequest.to;
    const facetSearch = new FacetSearch({
      searchBody: new Search({
        ...this.searchBody,
        ...reportRequest,
        keywords: this.contentKeywords || reportRequest.keywords || this.myCompanyQuery,
        timeframe: reportFrom ? 'CUSTOM' : reportRequest.timeframe,
        from: reportFrom ? new Date(reportFrom) : null,
        to: reportTo ? new Date(reportTo) : null,
        outlets: [outletItem.outletId],
      }),
    });

    this.store.dispatch(
      actions.loadOutletContent({
        facetSearch,
        outlet: {
          ...outletItem,
          id: outletItem.outletId,
          name: outletItem.name,
        },
      })
    );
  }

  protected getReportExportName(): string {
    return this.reportName;
  }

  getPage(e: any) {
    this.currentPage = e.page;
    this.page = e.page + 1;
    this.getOutletsData();
    if (this.table) {
      setTimeout(() => {
        this.table.scrollTo({ top: 0 });
      }, 300);
    }
  }

  sortChange(icon: any) {
    this.sortIcons.forEach(({ nativeElement }) => {
      if (eq(nativeElement, icon)) {
        if (eq(icon.id, 'sortNameIcon')) {
          icon.className = eq(icon.className, 'pi pi-sort')
            ? 'pi pi-sort-up'
            : eq(icon.className, 'pi pi-sort-up')
            ? 'pi pi-sort-down'
            : 'pi pi-sort-up';
        } else {
          icon.className = eq(icon.className, 'pi pi-sort')
            ? 'pi pi-sort-down'
            : eq(icon.className, 'pi pi-sort-up')
            ? 'pi pi-sort-down'
            : 'pi pi-sort-up';
        }
      } else {
        nativeElement.className = 'pi pi-sort';
      }
    });

    switch (icon.id) {
      case 'sortNameIcon':
        this.sort = eq(icon.className, 'pi pi-sort-up') ? 'name' : 'name,desc';
        this.sortColumn = 'name';
        break;

      case 'sortLastMentionedIcon':
        this.sort = eq(icon.className, 'pi pi-sort-up')
          ? 'maxDate'
          : 'maxDate,desc';
        this.sortColumn = 'maxDate';
        break;

      case 'sortFirstMentionedIcon':
        this.sort = eq(icon.className, 'pi pi-sort-up')
          ? 'minDate'
          : 'minDate,desc';
        this.sortColumn = 'minDate';
        break;

      case 'sortArticlesIcon':
        this.sort = eq(icon.className, 'pi pi-sort-up')
          ? 'numItems'
          : 'numItems,desc';
        this.sortColumn = 'numItems';
        break;

      case 'sortTotalCoverageIcon':
        this.sort = eq(icon.className, 'pi pi-sort-up')
          ? 'totalScore'
          : 'totalScore,desc';
        this.sortColumn = 'totalScore';
        break;

      default:
        this.sortColumn = '';
        break;
    }

    this.page = 1;
    this.getOutletsData();

    if (this.table) {
      setTimeout(() => {
        this.table.scrollTo({ top: 0 });
      }, 300);
    }
  }

  isOutletSaved(item: OutletReport): boolean {
    return !!find(this.savedOutlets, (o) => eq(o.partyId, item.outletId));
  }

  onOutletStarToggled(item: OutletReport, currentActive: boolean): void {
    this.savingOutletId = item.outletId;
    const userProject = { value: this.projectId };
    const outlet = { id: item.outletId, name: item.name };
    if (currentActive) {
      this.store.dispatch(actions.deleteOutletToProject({ partyId: item.outletId, userProject, outlet }));
    } else {
      this.store.dispatch(actions.addOutletToProject({ partyId: item.outletId, userProject, outlet }));
    }
  }

  goToTitleUrl(url) { window.open(url, '_blank'); }
}
